import type { IPromotionUiCatalog } from '../domain/types/promotions'
import { toPriceText, toPromotionBadge } from './catalogCard'
import type { PresentationContext, PromotionBadge } from './catalogCard'

export interface CartLineInput {
  /**
   * Stable identity for this *line*, not this product.
   *
   * The same sku can appear on two lines — bought as units and as a case — so a
   * sku is not a key. React renders two children with the same key as one, and
   * the stepper's draft state would follow whichever survived. Produced by
   * `toCartLineInputs`; a hand-built line can use its sku when the cart cannot
   * hold duplicates.
   */
  lineKey: string
  sku: string
  name: string
  image: string
  unitPrice: number
  quantity: number
  lineTotal: number
  isFreegood: boolean
  promotionUiDetails?: IPromotionUiCatalog | undefined
}

export interface CartRowModel {
  /** See `CartLineInput.lineKey`. This is what a list keys on. */
  lineKey: string
  sku: string
  title: string
  imageUrl: string
  quantity: number
  unitPriceText: string
  lineTotalText: string
  isFreegood: boolean
  canRemove: boolean
  promotionBadge: PromotionBadge | null
}

/**
 * The plan gives this file its own inline `money` helper and its own copy of the
 * promotion-badge branch. Both are the same logic the catalog card runs, so both
 * are imported from there instead: a storefront that changes how a hidden price
 * or a badge renders must not have two places to change it.
 */
export const toCartRowModel = (
  line: CartLineInput,
  ctx: PresentationContext,
): CartRowModel => ({
  lineKey: line.lineKey,
  sku: line.sku,
  title: line.name,
  imageUrl: line.image || ctx.placeholderImage,
  quantity: line.quantity,
  unitPriceText: toPriceText(line.unitPrice, ctx),
  lineTotalText: toPriceText(line.lineTotal, ctx),
  isFreegood: line.isFreegood,
  // Freegoods are granted by a promotion, so the shopper cannot remove them directly.
  canRemove: !line.isFreegood,
  promotionBadge: toPromotionBadge(line.promotionUiDetails),
})
